import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { getInput } from '../components/tasks/helpers';
import {
  getDeliveryQueryFn,
  type PackagingOption,
} from '../graphql/query/getDelivery';
import { useItems } from './useItems';
import { useReasons } from './useReasons';
import { QUERIES } from '../utils/constants';
import { hasItemsSelected as _hasItemsSelected } from '../workflow/schemaTypes';
import { useWorkflow } from '../workflow/hooks';
import { useFormContext } from 'react-hook-form';
import {
  selectDeliveryOptionMutationFn,
  type SelectDeliveryOptionResult,
} from '../graphql/mutation/selectDeliveryOption';
import { clientLogger } from '../utils/logger';
import { generateQueryKey } from '../utils/queries';
import { isTaskComplete } from '../utils/tasks';
import { useResolutions } from './useResolutions';
import { getParams } from '../utils/searchParams';
import { pdxEvent } from '../utils/noflake';
import type { DeliveryMethod } from '../components/RequestDetails/types';
import { useErrorHandler } from '../utils/handleErrorCodes';

export type UseDeliveryOptionsOpts = {
  onSuccessSetDeliveryOption?: (data?: SelectDeliveryOptionResult) => void;
  onErrorSetDeliveryOption?: (error: Error) => void;
};

export const useDeliveryOptions = (opts?: UseDeliveryOptionsOpts) => {
  const params = getParams();
  const { config, data, order, requestId, updateIsSaving } = useWorkflow();
  const handleErrorCodes = useErrorHandler();

  const { returnMethod } = data ?? {};

  const formContext = useFormContext();

  const items = useItems();
  const resolutionsByItem = useResolutions();
  const reasonsByItem = useReasons();

  const hasItemsSelected = _hasItemsSelected(data ?? {});
  const hasReasonsSelected = isTaskComplete(
    'reason',
    data ?? {},
    config,
    undefined,
  );
  const hasResolutionSelected = isTaskComplete(
    'resolution',
    data ?? {},
    config,
    undefined,
  );
  const hasReturnMethod = returnMethod && 'type' in returnMethod;

  const formPostalCode = formContext?.watch('locations.postalCode');
  const orderPostalCode = order?.shippingAddress?.postalCode;
  const postalCode = formPostalCode ?? orderPostalCode;

  const variables = getInput({
    requestId,
    customer: order?.customer,
    items,
    formItems: data?.items,
    resolutionsByItem,
    orderTokens: order?.orderTokens,
    orderId: order?.orderId,
    reasons: reasonsByItem,
  });

  const locationVars = postalCode
    ? {
        locations: {
          locale: params?.locale ?? 'en_US',
          postalCode,
        },
      }
    : {};

  const queryKey = generateQueryKey(QUERIES.GET_DELIVERY, {
    taskIds: ['reason', 'resolution'],
    workflowConfig: config,
    workflowData: data,
  });

  const { data: { deliveryOptions } = {}, isLoading } = useQuery({
    retry: false,
    retryOnMount: false,
    enabled: Boolean(
      variables.cart.id ||
      (order &&
        hasItemsSelected &&
        hasReasonsSelected &&
        hasResolutionSelected),
    ),
    queryKey: [...queryKey, postalCode],
    queryFn: async () => {
      try {
        return await getDeliveryQueryFn({
          ...variables,
          ...locationVars,
        });
      } catch (error: unknown) {
        handleErrorCodes(error);
        throw error;
      }
    },
  });

  // Auto-select NONE delivery method globally when it's the only option and no method is selected yet.
  useEffect(() => {
    const hasNoSelection = !hasReturnMethod || !returnMethod?.type;
    const onlyNone =
      Array.isArray(deliveryOptions) &&
      deliveryOptions.length === 1 &&
      deliveryOptions[0]?.type === 'NONE';
    if (hasNoSelection && onlyNone && formContext) {
      formContext.setValue('returnMethod.type', deliveryOptions[0]!.id, {
        shouldValidate: true,
        shouldDirty: true,
      });
      formContext.setValue('returnMethod.location', undefined, {
        shouldValidate: true,
        shouldDirty: true,
      });
      formContext.setValue('returnMethod.isAddressRequired', false, {
        shouldValidate: true,
        shouldDirty: true,
      });
      void formContext
        .trigger()
        .catch((err) =>
          clientLogger.logError(err, 'auto-select-none trigger failed'),
        );
    }
  }, [deliveryOptions, formContext, hasReturnMethod, returnMethod?.type]);

  const selectedMethodKey = hasReturnMethod ? returnMethod.type : undefined;
  const selectedMethod = deliveryOptions?.find(
    (option) => option.id === selectedMethodKey,
  );

  const locationProviderId = formContext?.watch('returnMethod.location');

  const { mutate: setDeliveryOption } = useMutation({
    mutationFn: async () => {
      if (!selectedMethod) {
        return;
      }

      // Skip SelectDeliveryOption call for NONE delivery types
      if (selectedMethod.type === 'NONE') {
        updateIsSaving(false);
        opts?.onSuccessSetDeliveryOption?.();
        return;
      }

      pdxEvent({
        event_category: 'set_data',
        event_type: 'set_delivery_option',
        event_value: 'delivery_option',
        delivery_type: selectedMethod?.type,
        delivery_method: selectedMethod.serviceProvider
          .moniker as DeliveryMethod,
        packaging_option: selectedMethod.packagingOption as PackagingOption,
        location_id: locationProviderId,
        task: 'returnMethod',
        step: 'returnMethod',
      });

      const hasLocations =
        selectedMethod.locations && selectedMethod.locations.length > 0;

      if (hasLocations && !locationProviderId) {
        return;
      }

      updateIsSaving(true);
      return selectDeliveryOptionMutationFn({
        cart: {
          id: requestId,
          delivery: {
            type: selectedMethod.type,
            serviceProviderId: selectedMethod.serviceProvider.moniker ?? '',
            service: selectedMethod.serviceProvider.name,
            packagingOption: selectedMethod.packagingOption,
            locationProviderId,
          },
          items: variables.cart.items,
        },
        orderTokens: variables.orderTokens,
      });
    },
    onSuccess: (data) => {
      pdxEvent({
        event_category: 'set_data',
        event_type: 'set_delivery_option_success',
        event_value: 'delivery_option',
        delivery_type: data?.selectDeliveryOption?.delivery?.type,
        delivery_method: data?.selectDeliveryOption?.delivery
          ?.serviceProviderId as DeliveryMethod,
        packaging_option: data?.selectDeliveryOption?.delivery
          ?.packagingOption as PackagingOption,
        location_id: data?.selectDeliveryOption?.delivery?.locationProviderId,
      });
      updateIsSaving(false);
      opts?.onSuccessSetDeliveryOption?.(data);
    },
    onError: (error: unknown) => {
      handleErrorCodes(error);
      pdxEvent({
        event_category: 'set_data',
        event_type: 'set_delivery_option_error',
        task: 'returnMethod',
        step: 'returnMethod',
      });
      clientLogger.logError(error);

      try {
        // Call the error callback first to handle network errors
        opts?.onErrorSetDeliveryOption?.(error as Error);
      } finally {
        // Clear the saving flag after the callback (even if it throws)
        updateIsSaving(false);
      }

      throw new Error('Something went wrong while saving the delivery option');
    },
  });

  return {
    isLoading,
    selectedMethod,
    selectedMethodKey,
    deliveryOptions,
    methodDropped:
      !isLoading &&
      hasReturnMethod &&
      !deliveryOptions?.some((method) => method.id === returnMethod?.type),
    orderPostalCode,
    setDeliveryOption,
  };
};
